/**
 * WebCrypto wrapper for Cuadrilla web (spec 200, Fase 2).
 *
 * Why a wrapper at all:
 *   `crypto.subtle` is everything we need (PBKDF2, AES-GCM, AES-CBC,
 *   HKDF, SHA-256, ECDH P-256), but each call needs a CryptoKey, an
 *   algorithm descriptor object, and a careful split between
 *   `deriveBits` and `deriveKey`. Inlining that across the codebase
 *   leads to subtle bugs (wrong `usages` array on import, swapping
 *   `info` for `salt`, forgetting to slice the GCM tag). This module
 *   centralises every primitive behind a Promise-returning function
 *   that takes Uint8Array and returns Uint8Array — call sites can
 *   stay focused on protocol semantics, not WebCrypto plumbing.
 *
 * Buffer story:
 *   The web is a browser environment; we standardise on `Uint8Array`
 *   end-to-end (no Node `Buffer`). Where mobile code uses `Buffer`,
 *   the equivalent here is `new Uint8Array(...)`. Hex/base64
 *   conversions live in the small helper block at the top.
 *
 * Algorithm choices:
 *   - PBKDF2-HMAC-SHA256, 100k iterations: matches mobile's
 *     `mnemonic.ts` / SEC-02 (the salt is the same too:
 *     `cuadrilla-salt-v1|<phrase>` -> sha256.slice(0,32)).
 *   - HKDF-SHA256 per RFC 5869: same primitive as mobile's
 *     `crypto/hkdf.ts`. Used by the pairing handshake of spec 200.
 *   - AES-GCM-256 (default for new payloads, SEC-21 forward-looking).
 *   - AES-CBC-256 (transitional decrypt path for blobs created by
 *     mobile builds older than SEC-21). We only expose the *decrypt*
 *     side: never encrypt new ciphertext as CBC from web — if a write
 *     leaves the web, it should already be in the post-SEC-21 format.
 *   - ECDH P-256: same curve as mobile's `quickCrypto.createECDH('prime256v1')`
 *     (P-256 == prime256v1 == secp256r1).
 *
 * Things this file deliberately does NOT do:
 *   - Cache CryptoKey instances. A higher-level module (the team-key
 *     vault, IndexedDB store) owns lifetime; this layer is stateless.
 *   - Throw "user-friendly" Spanish errors. WebCrypto rejections are
 *     opaque DOMExceptions; we re-throw them verbatim and let the
 *     UI translate at the call site.
 *   - Random byte generation other than IVs. Use `crypto.getRandomValues`
 *     directly at the call site for ephemeral keys, nonces, etc.
 */

// ── Hex/byte helpers ─────────────────────────────────────────────────
//
// Cheap conversions kept here because:
//   - importing a 30 KB hex library for these is silly,
//   - every call site needs the same shape, and
//   - inlining them into the primitives below makes the function
//     bodies harder to skim.

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new RangeError(`hexToBytes: odd-length input "${hex}"`);
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new RangeError(`hexToBytes: invalid hex at offset ${i * 2}`);
    out[i] = byte;
  }
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

export function utf8ToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

export function bytesToUtf8(bytes: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

// ── Subtle wrapper ───────────────────────────────────────────────────

const subtle: SubtleCrypto = globalThis.crypto.subtle;

/**
 * Copy a Uint8Array into a fresh ArrayBuffer-backed Uint8Array.
 *
 * Two reasons we always copy at the SubtleCrypto boundary:
 *
 * 1) `BufferSource` (the parameter type for every `subtle.*` call) was
 *    narrowed in recent `lib.dom.d.ts` to `ArrayBufferView<ArrayBuffer>`
 *    (i.e. NOT `SharedArrayBuffer`-backed). A Uint8Array constructed
 *    on top of a Node `Buffer` (which we get from happy-dom test
 *    fixtures or any `Buffer.from(...)` call site) reports
 *    `ArrayBufferLike` — the union — and TS rejects it. Re-wrapping
 *    the bytes around a fresh `ArrayBuffer` gives the type checker an
 *    unambiguous `Uint8Array<ArrayBuffer>` and matches what every
 *    browser actually accepts at runtime.
 *
 * 2) A view with a non-zero `byteOffset` is also a runtime gotcha on
 *    some engines — same fix.
 */
function clone(view: Uint8Array): Uint8Array<ArrayBuffer> {
  const buffer = new ArrayBuffer(view.byteLength);
  const out = new Uint8Array(buffer);
  out.set(view);
  return out;
}

// ── PBKDF2 ────────────────────────────────────────────────────────────

/**
 * PBKDF2-HMAC-SHA256.
 *
 * Mobile uses 100 000 iterations as the SEC-02 baseline; the same
 * value here keeps recovery-phrase derivations identical across
 * platforms (same phrase + same salt -> same 32-byte key on web and
 * mobile).
 *
 * @param password Raw password material — the recovery phrase or any
 *                 high-entropy seed. Caller is responsible for
 *                 normalisation (NFC, lowercase) BEFORE calling.
 * @param salt     Caller-provided salt; SEC-02 uses
 *                 `sha256('cuadrilla-salt-v1|' + words).slice(0,32)`.
 * @param iterations Default 100k; bump only on coordinated migration.
 * @param lengthBytes Default 32 (AES-256 key size).
 */
export async function pbkdf2(
  password: Uint8Array,
  salt: Uint8Array,
  iterations: number = 100_000,
  lengthBytes: number = 32,
): Promise<Uint8Array> {
  if (iterations <= 0)
    throw new RangeError(`pbkdf2: iterations must be positive (got ${iterations})`);
  if (lengthBytes <= 0)
    throw new RangeError(`pbkdf2: lengthBytes must be positive (got ${lengthBytes})`);

  const keyMaterial = await subtle.importKey('raw', clone(password), { name: 'PBKDF2' }, false, [
    'deriveBits',
  ]);
  const bits = await subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: clone(salt), iterations },
    keyMaterial,
    lengthBytes * 8,
  );
  return new Uint8Array(bits);
}

// ── AES-GCM ───────────────────────────────────────────────────────────

export interface AesGcmCiphertext {
  /** GCM ciphertext WITHOUT the authentication tag. */
  ciphertext: Uint8Array;
  /** 12-byte IV (GCM canonical length). */
  iv: Uint8Array;
  /** 16-byte authentication tag, separated from the ciphertext for
   *  wire compatibility with relay payloads that carry it as a distinct
   *  field. */
  tag: Uint8Array;
}

/**
 * AES-GCM-256 encrypt with a randomly-generated 12-byte IV (unless
 * caller supplies one — useful for deterministic test vectors and
 * NEVER in production).
 *
 * Output shape `{ ciphertext, iv, tag }`:
 *   WebCrypto returns `ciphertext || tag` as one buffer. We split it
 *   so call sites that wire the relay's `{ ct, iv, tag }` format can
 *   field the output directly. The split point is the last 16 bytes
 *   (GCM canonical tag length).
 *
 * @param key       32-byte raw AES key. Pass a `CryptoKey` is also
 *                  supported via overload for callers that already hold
 *                  one (avoids the import roundtrip).
 * @param plaintext Bytes to encrypt.
 * @param iv        Optional 12-byte IV. Generated randomly when omitted.
 * @param aad       Optional Additional Authenticated Data. Bound into
 *                  the GCM tag — same bytes must be passed to
 *                  `aesGcmDecrypt`, otherwise authentication fails.
 *                  Use this for relay/pairing wire formats that carry
 *                  context (sessionId, version, recipient) outside
 *                  the ciphertext but still need integrity coverage.
 *                  Pass `undefined` (or omit) when not used; the
 *                  decrypt side must match.
 */
export async function aesGcmEncrypt(
  key: Uint8Array | CryptoKey,
  plaintext: Uint8Array,
  iv?: Uint8Array,
  aad?: Uint8Array,
): Promise<AesGcmCiphertext> {
  const cryptoKey =
    key instanceof Uint8Array ? await importAesKey(key, 'AES-GCM', ['encrypt']) : key;
  const ivBytes = iv ?? globalThis.crypto.getRandomValues(new Uint8Array(12));
  if (ivBytes.length !== 12) {
    throw new RangeError(`aesGcmEncrypt: IV must be 12 bytes (got ${ivBytes.length})`);
  }
  // The `additionalData` field of `AesGcmParams` is optional. When the
  // caller didn't pass `aad`, we MUST omit the field entirely from the
  // params object — passing `additionalData: undefined` flips some
  // implementations into "AAD = empty buffer" mode, which won't match
  // a decryptor that also omits the field. Keeping the keys symmetric
  // on both sides is the contract.
  const params: AesGcmParams = aad
    ? { name: 'AES-GCM', iv: clone(ivBytes), additionalData: clone(aad) }
    : { name: 'AES-GCM', iv: clone(ivBytes) };
  const combined = new Uint8Array(await subtle.encrypt(params, cryptoKey, clone(plaintext)));
  // GCM tag is the trailing 16 bytes per RFC 5288.
  const ciphertext = combined.slice(0, combined.length - 16);
  const tag = combined.slice(combined.length - 16);
  return { ciphertext, iv: clone(ivBytes), tag };
}

export async function aesGcmDecrypt(
  key: Uint8Array | CryptoKey,
  ciphertext: Uint8Array,
  iv: Uint8Array,
  tag: Uint8Array,
  aad?: Uint8Array,
): Promise<Uint8Array> {
  if (iv.length !== 12)
    throw new RangeError(`aesGcmDecrypt: IV must be 12 bytes (got ${iv.length})`);
  if (tag.length !== 16)
    throw new RangeError(`aesGcmDecrypt: tag must be 16 bytes (got ${tag.length})`);
  const cryptoKey =
    key instanceof Uint8Array ? await importAesKey(key, 'AES-GCM', ['decrypt']) : key;
  // SubtleCrypto wants `ciphertext || tag` as one input — concatenate
  // here so wire formats that carry them split can stay split.
  const combined = new Uint8Array(ciphertext.length + tag.length);
  combined.set(ciphertext, 0);
  combined.set(tag, ciphertext.length);
  // Same shape rule as encrypt: omit `additionalData` entirely when
  // not supplied. A mismatched present/absent on the two sides
  // surfaces as an authentication failure (DOMException), which is
  // the desired behaviour — AAD acts as part of the integrity check.
  const params: AesGcmParams = aad
    ? { name: 'AES-GCM', iv: clone(iv), additionalData: clone(aad) }
    : { name: 'AES-GCM', iv: clone(iv) };
  const plain = await subtle.decrypt(params, cryptoKey, combined);
  return new Uint8Array(plain);
}

// ── AES-CBC (transitional) ───────────────────────────────────────────

/**
 * AES-256-CBC encrypt with PKCS#7 padding.
 *
 * Used by the web sync layer (`sync/syncWork.ts`) to produce blobs
 * that mobile peers running pre-SEC-21 code can decrypt. Once SEC-21
 * is universal both encrypt + decrypt halves can be removed and the
 * sync moves to AES-GCM.
 *
 * @param key 32-byte raw AES key.
 * @param plaintext Bytes to encrypt.
 * @param iv 16-byte IV. CALLER provides it so a fresh `getRandomValues`
 *           can produce a deterministic test fixture or come from an
 *           external entropy source. Production call sites pass a
 *           freshly random 16-byte buffer.
 */
export async function aesCbcEncrypt(
  key: Uint8Array,
  plaintext: Uint8Array,
  iv: Uint8Array,
): Promise<Uint8Array> {
  if (iv.length !== 16)
    throw new RangeError(`aesCbcEncrypt: IV must be 16 bytes (got ${iv.length})`);
  const cryptoKey = await importAesKey(key, 'AES-CBC', ['encrypt']);
  const ct = await subtle.encrypt(
    { name: 'AES-CBC', iv: clone(iv) },
    cryptoKey,
    clone(plaintext),
  );
  return new Uint8Array(ct);
}

/**
 * AES-256-CBC decrypt with PKCS#7 padding. ONLY for unwrapping legacy
 * blobs produced by mobile builds older than SEC-21. The web never
 * writes CBC ciphertext — once SEC-21 finishes rolling out everywhere,
 * this function can be removed.
 *
 * @param key 32-byte raw AES key.
 * @param ciphertext PKCS#7-padded ciphertext (length must be a multiple of 16).
 * @param iv 16-byte IV.
 */
export async function aesCbcDecrypt(
  key: Uint8Array,
  ciphertext: Uint8Array,
  iv: Uint8Array,
): Promise<Uint8Array> {
  if (iv.length !== 16)
    throw new RangeError(`aesCbcDecrypt: IV must be 16 bytes (got ${iv.length})`);
  if (ciphertext.length === 0 || ciphertext.length % 16 !== 0) {
    throw new RangeError(
      `aesCbcDecrypt: ciphertext length must be a positive multiple of 16 (got ${ciphertext.length})`,
    );
  }
  const cryptoKey = await importAesKey(key, 'AES-CBC', ['decrypt']);
  const plain = await subtle.decrypt(
    { name: 'AES-CBC', iv: clone(iv) },
    cryptoKey,
    clone(ciphertext),
  );
  return new Uint8Array(plain);
}

// ── SEC-21 — format-aware string dispatcher ───────────────────────────
//
// Mobile (`mobile/src/crypto/encryption.ts`) wraps every sync blob in
// the same wire format the web reads from `sync` messages and
// `/photos/:id` responses. Phase A of SEC-21 introduces the GCM
// version literal `g1:` so receivers can distinguish authenticated
// (GCM) ciphertexts from legacy CBC ones during the rollout window.
//
//   - Legacy CBC (no prefix):   `ivHex:base64Ciphertext`
//   - GCM (versioned prefix):   `g1:ivHex:tagHex:base64Ciphertext`
//
// `decryptStringAuto` is the single place every sync route should
// call; routing via the prefix keeps the format detection in one
// audited spot. New encrypts on the web stay CBC for now (Phase A);
// flipping to GCM is a one-line change in `syncWork.ts` once mobile
// + relay metrics confirm rollout.

const GCM_STRING_PREFIX = 'g1:';

export async function decryptStringAuto(
  teamKey: Uint8Array,
  payload: string,
): Promise<Uint8Array> {
  if (payload.startsWith(GCM_STRING_PREFIX)) {
    const body = payload.slice(GCM_STRING_PREFIX.length);
    const parts = body.split(':');
    if (parts.length !== 3) {
      throw new Error(
        'decryptStringAuto: malformed GCM payload (expected ivHex:tagHex:base64)',
      );
    }
    const [ivHex, tagHex, cipherB64] = parts;
    const iv = hexToBytes(ivHex);
    const tag = hexToBytes(tagHex);
    if (iv.length !== 12) {
      throw new RangeError(`decryptStringAuto: GCM IV must be 12 bytes (got ${iv.length})`);
    }
    if (tag.length !== 16) {
      throw new RangeError(`decryptStringAuto: GCM tag must be 16 bytes (got ${tag.length})`);
    }
    const cipher = base64ToBytes(cipherB64);
    return aesGcmDecrypt(teamKey, cipher, iv, tag);
  }
  // Legacy CBC fallback. Same layout the suite has used since SEC-11.
  const [ivHex, cipherB64] = payload.split(':');
  if (!ivHex || !cipherB64) {
    throw new Error('decryptStringAuto: malformed CBC payload (expected ivHex:base64)');
  }
  const iv = hexToBytes(ivHex);
  const cipher = base64ToBytes(cipherB64);
  return aesCbcDecrypt(teamKey, cipher, iv);
}

function base64ToBytes(b64: string): Uint8Array {
  // `atob` works in every browser the suite targets and in
  // happy-dom / jsdom. No Buffer dependency in browser code.
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function importAesKey(
  raw: Uint8Array,
  algorithm: 'AES-GCM' | 'AES-CBC',
  usages: KeyUsage[],
): Promise<CryptoKey> {
  if (raw.length !== 16 && raw.length !== 24 && raw.length !== 32) {
    throw new RangeError(`AES key must be 16/24/32 bytes (got ${raw.length})`);
  }
  return subtle.importKey('raw', clone(raw), { name: algorithm }, false, usages);
}

// ── HKDF-SHA256 ──────────────────────────────────────────────────────

/**
 * HKDF-SHA256 per RFC 5869.
 *
 * Behaviour matches `mobile/src/crypto/hkdf.ts` exactly: empty salt
 * uses the 32-byte zero-string default per §2.2; `info` is accepted
 * as `string` (UTF-8 encoded internally) or raw bytes for RFC 5869
 * §A.1–A.3 conformance tests.
 *
 * Note: `length === 0` is rejected because the WebCrypto `deriveBits`
 * implementation rejects it too, but with a less useful error. The
 * mobile wrapper is identical.
 */
export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: string | Uint8Array,
  length: number,
): Promise<Uint8Array> {
  if (length <= 0 || length > 255 * 32) {
    throw new RangeError(`hkdfSha256: invalid length ${length}`);
  }
  const ikmKey = await subtle.importKey('raw', clone(ikm), { name: 'HKDF' }, false, ['deriveBits']);
  // The `salt: BufferSource` and `info: BufferSource` branches of
  // HkdfParams need to land on `ArrayBuffer`-backed Uint8Arrays;
  // `clone()` enforces that. The "default 32-byte zero salt" path of
  // RFC 5869 §2.2 is built on a fresh ArrayBuffer for the same reason.
  const saltBytes: Uint8Array<ArrayBuffer> =
    salt.length > 0 ? clone(salt) : new Uint8Array(new ArrayBuffer(32));
  const infoBytes: Uint8Array<ArrayBuffer> =
    typeof info === 'string' ? clone(utf8ToBytes(info)) : clone(info);
  const bits = await subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: saltBytes, info: infoBytes },
    ikmKey,
    length * 8,
  );
  return new Uint8Array(bits);
}

// ── SHA-256 ──────────────────────────────────────────────────────────

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const digest = await subtle.digest('SHA-256', clone(data));
  return new Uint8Array(digest);
}

// ── ECDH P-256 ───────────────────────────────────────────────────────
//
// Mobile uses `quickCrypto.createECDH('prime256v1')` which produces
// SEC1-uncompressed public keys (65 bytes: `0x04 || X(32) || Y(32)`).
// The `raw` import format in WebCrypto consumes that exact byte
// layout for P-256 ECDH keys, so wire compatibility with the mobile
// pairing handshake is one-to-one.

export interface EcdhKeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

export const ecdhP256 = {
  async generateKeyPair(): Promise<EcdhKeyPair> {
    const pair = (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
      'deriveBits',
    ])) as CryptoKeyPair;
    return { publicKey: pair.publicKey, privateKey: pair.privateKey };
  },

  /**
   * Export a P-256 public key as the SEC1-uncompressed 65-byte form
   * mobile's `quickCrypto.createECDH(...).getPublicKey('hex')` produces
   * (then hex-encoded). Workers parse `webPub` from the QR; admins
   * parse `mobilePub` from `pair_sessions`. Same shape on both sides.
   */
  async exportPublicKey(key: CryptoKey): Promise<Uint8Array> {
    const raw = await subtle.exportKey('raw', key);
    return new Uint8Array(raw);
  },

  /**
   * Import a peer's SEC1-uncompressed P-256 public key.
   */
  async importPublicKey(rawBytes: Uint8Array): Promise<CryptoKey> {
    if (rawBytes.length !== 65 || rawBytes[0] !== 0x04) {
      throw new RangeError(
        `ecdhP256.importPublicKey: expected 65-byte SEC1-uncompressed (0x04 || X || Y), got ${rawBytes.length} bytes`,
      );
    }
    return subtle.importKey(
      'raw',
      clone(rawBytes),
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
  },

  /**
   * ECDH(privA, pubB) -> 32-byte shared secret (the X coordinate of
   * the resulting point). Matches `mobile`'s
   * `ecdh.computeSecret(adminPub, 'hex')` byte-for-byte for the same
   * inputs.
   *
   * IMPORTANT: never feed this output directly into AES — funnel it
   * through `hkdfSha256(secret, salt=joinId, info='cuadrilla-...', 32)`
   * first (SEC-22 / spec 200). The plain X coordinate has structural
   * bias when used as a key.
   */
  async sharedSecret(privateKey: CryptoKey, publicKey: CryptoKey): Promise<Uint8Array> {
    const bits = await subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
    return new Uint8Array(bits);
  },
};
